/**
 * User-facing settings and their defaults.
 *
 * Two groups with very different rules:
 *
 *   - `alcoholPromille` / `drugsPercent` CHANGE THE GAME. They are part of the
 *     run's identity: personal bests are bucketed per dose, and changing either
 *     one restarts the run.
 *   - everything else is comfort only. `cameraSwayIntensity` in particular
 *     scales the *camera* and nothing else, so lowering it cannot make the game
 *     easier to race.
 */

import { DEFAULT_INTOXICATION } from './intoxication';

export interface GameSettings {
  /**
   * Name shown on the shared leaderboard. Comfort-adjacent rather than comfort:
   * it changes nothing about the run, but without one there is nobody to put on
   * the board, so the game asks for it before the first submission.
   */
  playerName: string;

  /** Blood alcohol in promille, 0.00 - 5.00 in 0.01 steps. Affects movement. */
  alcoholPromille: number;
  /** Psychedelic dose 0 - 100 %. Affects rendering only. */
  drugsPercent: number;

  /** Multiplier on LOOK.baseSensitivity. */
  mouseSensitivity: number;
  /** Reverse vertical mouse input before recording look angles. */
  invertMouse: boolean;
  /** 0 = locked camera, 1 = default, 2 = seasick. Cosmetic only. */
  cameraSwayIntensity: number;
  fieldOfView: number;
  audioEnabled: boolean;
  showDebugOverlay: boolean;
  showColliders: boolean;
}

export const DEFAULT_SETTINGS: GameSettings = {
  playerName: '',
  alcoholPromille: DEFAULT_INTOXICATION.alcoholPromille,
  drugsPercent: DEFAULT_INTOXICATION.drugsPercent,
  mouseSensitivity: 1.0,
  invertMouse: true,
  cameraSwayIntensity: 1.0,
  fieldOfView: 82,
  audioEnabled: true,
  showDebugOverlay: false,
  showColliders: false,
};

export const SETTINGS_LIMITS = {
  mouseSensitivity: { min: 0.2, max: 3.0, step: 0.05 },
  cameraSwayIntensity: { min: 0, max: 2.0, step: 0.05 },
  fieldOfView: { min: 65, max: 110, step: 1 },
} as const;

/** Presentation-only range; exact competition seeds can use the full uint32 range. */
export const SEED_SLIDER_MAX = 10_000;

export const SETTINGS_STORAGE_KEY = 'laniaamu.settings';
